import React, { Component, ReactNode } from 'react';
import { ConfirmPopup } from 'primereact/confirmpopup';
import CustomTable, { CustomColumnProps } from '../custom/CustomTable';
import FolderView from '../folder/view/FolderView';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import LanguageUtil from '../../util/LanguageUtil';
import RoleHelper from '../../RoleHelper';
import ListDraggableItem from '../custom/ListDraggableItem';
import { dispatch, RootState } from '../../redux/store';
import { ComponentState, setSelectedItems } from '../../redux/actions/ComponentActions';
import { connect, ConnectedProps } from 'react-redux';
import { customConfirm } from '../custom/CustomPopupConfirm';
import Icon from '../../icons/Icon';
import { query } from '@trlogic/client-js';
import ComponentName from '../../constant/ComponentName';
import { PermissionType } from '../../domain/rbac/RbacDomains';
import CustomTableContentWrapper from '../custom/CustomTableContentWrapper';
import { Tooltip } from 'primereact/tooltip';
import ComponentUtil from '../../util/ComponentUtil';
import moment from 'moment/moment';
import AchFileUpload from './AchFileUpload';
import { AchItem } from '../../client/ach/AchClient';
import AchService from '../../service/AchService';
import ServiceContainer from 'config/ServiceContainer';
import { ServiceNames } from 'constant/ServiceNames';
import CustomPaginator, { CustomPaginatorProps } from '../custom/CustomPaginator';
import MessageUtil from '../../util/MessageUtil';
import Order = query.Order;

interface FileUploadResult {
  fileName: string;
  success: boolean;
  error?: string;
}

interface UploadSummary {
  uploaded: FileUploadResult[];
  failed: FileUploadResult[];
}

const COMPONENT_NAME = 'ACH';

class AchListViewerComponent extends Component<Props> {
  private readonly achService: AchService = ServiceContainer.getInstance().get(ServiceNames.ACH);

  public render(): ReactNode {
    const {
      views,
      achItems,
      loading,
      component: { selectedComponentIds },
      onSortChange,
    } = this.props;

    return (
      <div className={'flex flex-col w-full h-full'}>
        <div className={'flex-1 overflow-y-auto min-h-0 rounded-lg px-4 2xl:px-5'}>
          <ConfirmPopup />
          <CustomTable
            value={achItems}
            selectionMode='checkbox'
            selection={selectedComponentIds}
            onSelectionChange={this.onSelectionChange}
            rowClassName={(data) => {
              const view = views.find((v: FolderView) => v.getModel().value?.payload.name == data.name);
              return view ? `list_${view?.getModel().id}` : '';
            }}
            componentName={ComponentName.ACH}
            openNewTab={true}
            loading={loading}
            onRowClick={this.openEditor}
            columns={this.getColumns()}
            onSortChange={onSortChange}
          />

          <ListDraggableItem itemType={ComponentName.ACH} currentModal={this.props.currentModal} />
        </div>
        {this.props.totalRecord !== undefined && this.props.totalRecord > 0 && this.props.onPageChange && (
          <div className={'sticky bottom-0 z-10 bg-white'}>
            <CustomPaginator
              rows={this.props.rows || 20}
              totalRecords={this.props.totalRecord}
              currentRecords={achItems.length}
              rowsPerPageOptions={[5, 10, 20, 50, 100]}
              onPageChange={this.props.onPageChange}
              currentPage={this.props.currentPage || 1}
              loading={loading}
              fixed={true}
            />
          </div>
        )}
        {(!achItems || achItems.length === 0) && !loading && (
          <div className={'flex w-full h-full items-center justify-center'}>
            <div className='w-full h-full px-4 2xl:px-5 pb-3'>
              <AchFileUpload onFilesSelect={this.onFilesSelect} />
            </div>
          </div>
        )}
      </div>
    );
  }

  private onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };

  private openEditor = (e: DataTableRowClickEventParams) => {
    const { openAch, router } = this.props;
    const achItem: AchItem = e.data;

    if (achItem.fileName && router) {
      router.navigate(`/ach/${achItem.fileName}`);
      return;
    }

    const id = achItem.id;
    openAch(id);
  };

  private checkFileExists = (fileName: string): boolean => {
    return this.props.achItems.some((item: AchItem) => item.fileName === fileName);
  };

  private extractErrorMessage = (error: unknown): string => {
    if (!(error instanceof Error)) {
      return LanguageUtil.get({
        id: 'common.error.unknownError',
        defaultMessage: 'An unknown error occurred',
      });
    }

    const errorMessage = error.message;
    if (errorMessage.includes('already exists')) {
      return LanguageUtil.alreadyExist(COMPONENT_NAME, '');
    }

    return LanguageUtil.get({
      id: 'common.error.achFileUploadFailed',
      defaultMessage: 'Failed to upload ACH file',
    });
  };

  private uploadFileSequentially = async (file: File): Promise<FileUploadResult> => {
    const fileName = file.name;

    if (this.checkFileExists(fileName)) {
      return {
        fileName,
        success: false,
        error: LanguageUtil.alreadyExist(COMPONENT_NAME, fileName),
      };
    }

    try {
      await this.achService.uploadAchFile(file);
      return {
        fileName,
        success: true,
      };
    } catch (error: unknown) {
      console.error(`Error uploading file ${fileName}:`, error);
      return {
        fileName,
        success: false,
        error: this.extractErrorMessage(error),
      };
    }
  };

  private processFilesSequentially = async (files: File[]): Promise<UploadSummary> => {
    const summary: UploadSummary = {
      uploaded: [],
      failed: [],
    };

    for (const file of files) {
      const result = await this.uploadFileSequentially(file);
      if (result.success) {
        summary.uploaded.push(result);
      } else {
        summary.failed.push(result);
      }
    }

    return summary;
  };

  private showUploadResults = (summary: UploadSummary): void => {
    if (summary.uploaded.length > 0) {
      const successMessage = LanguageUtil.get(
        {
          id: 'common.message.filesUploadedSuccessfully',
          defaultMessage: '{count} file(s) uploaded successfully',
        },
        { count: summary.uploaded.length },
      );

      MessageUtil.success({
        summary: LanguageUtil.get({
          id: 'pages.ach.table.achFileName',
          defaultMessage: 'ACH File Name',
        }),
        detail: successMessage,
      });
    }

    if (summary.failed.length > 0) {
      summary.failed.forEach((result) => {
        MessageUtil.error({
          summary: LanguageUtil.get({
            id: 'pages.ach.table.achFileName',
            defaultMessage: 'ACH File Name',
          }),
          detail: `${result.fileName}: ${result.error || LanguageUtil.get({
            id: 'common.error.uploadFailed',
            defaultMessage: 'Upload failed',
          })}`,
        });
      });
    }
  };

  private onFilesSelect = async (files: File[]): Promise<void> => {
    if (!files || files.length === 0) {
      return;
    }

    const summary = await this.processFilesSequentially(files);
    this.showUploadResults(summary);

    if (summary.uploaded.length > 0 && this.props.onFileUploaded) {
      await this.props.onFileUploaded();
    }
  };

  private getColumns = (): CustomColumnProps[] => {
    const columns: CustomColumnProps[] = [
      {
        field: 'fileName',
        header: LanguageUtil.get({ id: 'pages.ach.table.achFileName', defaultMessage: 'ACH File Name' }),
        className: 'min-w-[20%]',
        copyable: true,
        onCopy: (e: AchItem) => {
          return e.fileName;
        },
        body: (record: AchItem) => {
          const { views } = this.props;
          const view: FolderView | undefined = views.find((v: FolderView) => v.getModel().value?.payload.name == record.id);
          return (
            <div className={'flex items-start text-xs 2xl:text-md'} id={``}>
              <div id={`list_${view?.getModel().id}`} />
              <CustomTableContentWrapper text={record.fileName} data-testid={`${ComponentName.ACH}_fileName_text_${record.id}`} />
            </div>
          );
        },
      },
      {
        field: 'stage',
        header: LanguageUtil.get({ id: 'pages.ach.table.status', defaultMessage: 'Status' }),
        className: 'min-w-[20%]',
        body: (record: AchItem) => {
          let statusStyle = '';
          let statusText = '';

          switch (record.stage) {
            case 'DONE':
              statusText = 'Done';
              statusStyle = 'bg-[#DDFBEA] text-[#037A48]';
              break;
            case 'IN_PROGRESS':
              statusText = 'In Progress';
              statusStyle = 'bg-[#EFF8FF] text-[#175CD3]';
              break;
            case 'FAILED':
              statusText = 'Failed';
              statusStyle = 'bg-[#FFF1F3] text-[#D2332F]';
              break;
            case 'SCHEDULED':
            default:
              statusStyle = 'bg-gray-100 text-gray-800';
              break;
          }
          return (
            <div className={`${statusStyle} flex w-[102px] h-6 items-center text-xs 2xl:text-md justify-center relative group rounded-full`}>
                <span>{statusText}</span>
            </div>
          );
        },
      },
      {
        field: 'uploader',
        header: LanguageUtil.get({ id: 'pages.ach.table.uploader', defaultMessage: 'Uploader' }),
        className: 'min-w-[25%]',
        body: (record: AchItem) => {
          const uploader = record.uploader;
          if (uploader === undefined || uploader === null || uploader === '') {
            return (
              <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
                <CustomTableContentWrapper
                  text={LanguageUtil.get({ id: 'pages.ach.table.noOneAssigned', defaultMessage: 'No one assigned' })}
                  data-testid={`${ComponentName.ACH}_uploader_text_${record.id}`}
                />
              </div>
            );
          }
          const getInitials = (first?: string, last?: string): string => {
            const firstInitial = first && first.length > 0 ? first.substring(0, 1).toUpperCase() : '';
            const lastInitial = last && last.length > 0 ? last.substring(0, 1).toUpperCase() : '';
            return firstInitial + lastInitial || (uploader ? uploader.substring(0, 1).toUpperCase() : '');
          };
          const initials = getInitials(record.firstName, record.lastName);
          return (
            <div className={'flex w-full items-center text-f-black-3 text-xs 2xl:text-md justify-start -ml-1 relative group gap-x-1'}>
              <div className={'flex items-center text-f-black-3 text-xs 2xl:text-md'}>
                <div className={'w-7 h-7 flex items-center justify-center rounded-full bg-[#F4F3FF] mr-1'}>{initials}</div>
              </div>
              <CustomTableContentWrapper text={`@${uploader}`} data-testid={`${ComponentName.ACH}_uploader_text_${record.id}`} />
            </div>
          );
        },
      },
      {
        field: 'uploadedAt',
        header: LanguageUtil.get({ id: 'pages.ach.table.createdAt', defaultMessage: 'Created At' }),
        className: 'min-w-[25%]',
        body: (record: AchItem) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.createdAt ? moment(record.createdAt).format('LLL') : ''} />
            </div>
          );
        },
      }
    ];
    return columns.filter((column) => {
      if (column.field === 'action') {
        return RoleHelper.canEdit('ACH');
      }
      return true;
    });
  };
}

interface Props extends ReduxProps {
  achItems: AchItem[];
  openAch: (id: string) => void;
  views: FolderView[];
  currentModal: any;
  onSortChange: (order: Order[]) => void;
  permissions: Record<string, PermissionType>;
  loading?: boolean;
  onFileUploaded?: () => Promise<void>;
  router?: {
    navigate: (to: string) => void;
    params: Record<string, string>;
    location: Location;
  };
  rows?: number;
  totalRecord?: number;
  currentPage?: number;
  onPageChange?: (event: CustomPaginatorProps) => void;
}

const mapStateToProps = (state: RootState) => ({
  component: state.component as ComponentState,
});
const mapDispatchToProps = {};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(AchListViewerComponent);
